import type { FeatureTriggerPort } from "./FeatureTriggerPort";
import type { FeatureTriggerTuning } from "./FeatureTriggerTuning";
import type { AudioService } from "../../audio/ports/AudioService";
import type { InteractionBlockStore } from "../../interaction-block/InteractionBlockStore";
import type { CanvasOverlayStore } from "../../canvas-overlay/CanvasOverlayStore";
import { OverlayPressBinding } from "../overlayInput";
import type { Log, PresentationTaskRunner } from "@slot/presentation";

export class FeatureTriggerPresenter {
  private readonly panel: FeatureTriggerPort;
  private readonly log: Log;
  private readonly audioService: AudioService;
  private readonly interactionBlockStore: InteractionBlockStore;
  private readonly tuning: FeatureTriggerTuning;
  /** The chrome runner: the arm delay and the music beat pause and frame-step with the game, never speed-scale. */
  private readonly runner: PresentationTaskRunner;
  private readonly canvasOverlayStore: CanvasOverlayStore;

  private closeRequested = false;
  private resolveClose: (() => void) | null = null;

  private readonly pressBinding = new OverlayPressBinding();
  private unbindSkipTap: (() => void) | null = null;
  private skipShown = false;

  public constructor(input: {
    panel: FeatureTriggerPort;
    audioService: AudioService;
    interactionBlockStore: InteractionBlockStore;
    /** The panel is Pixi: the DOM chrome hides while it is up. */
    canvasOverlayStore: CanvasOverlayStore;
    log: Log;
    /** All client presentation knobs (arm delay + music ids/fades) — the single tuning object. */
    tuning: FeatureTriggerTuning;
    runner: PresentationTaskRunner;
  }) {
    this.runner = input.runner;
    this.canvasOverlayStore = input.canvasOverlayStore;
    this.panel = input.panel;
    this.log = input.log;
    this.audioService = input.audioService;
    this.interactionBlockStore = input.interactionBlockStore;
    this.tuning = input.tuning;
  }

  public async presentFeatureWon(
    featureId: string,
    featureCount: number | string,
    onDismissed?: () => void,
    /**
     * True when this trigger fires while the feature is already active
     * (free-spins retrigger). The feature loop music is already the current
     * bed, so the idle/loop crossfades are skipped — the loop continues
     * uninterrupted. Visual flow and interaction block are unchanged; the
     * caller is responsible for also skipping the plasm transition on
     * retrigger if desired (by omitting `onDismissed`).
     */
    isRetrigger?: boolean,
  ): Promise<void> {
    this.closeRequested = false;
    this.resolveClose = null;

    this.applyContent(featureId, featureCount);

    this.panel.show();
    if (this.tuning.openSoundId) this.audioService.presentation.playSoundId?.(this.tuning.openSoundId);
    this.skipShown = false;
    this.panel.setSkipEnabled(false);
    this.interactionBlockStore.add("feature_trigger_panel");

    // Crossfade base → idle music as the panel appears. Short delay so the
    // music change doesn't compete with the panel's enter animation, and a
    // 1.5s fade for a gentle swell rather than an abrupt switch. On dismiss
    // (after the exit animation), crossfade idle → feature music so the
    // next free spin starts on the feature loop. Both calls are idempotent
    // and survive any redundant `setFeatureState` event that follows.
    // Skipped on retrigger: the feature loop is already playing.
    if (!isRetrigger) {
      void this.runner.sleep(this.tuning.music.idleFadeInDelayMs / 1000).then(() => {
        this.audioService.control.setMusicTrack(this.tuning.music.idleId, this.tuning.music.idleFadeMs);
      });
    }

    this.bindInput();
    const releaseOverlay = this.canvasOverlayStore.open();

    try {
      await this.panel.playEnterAnimation();

      await this.runner.sleep(this.tuning.armDelayMs / 1000);

      this.skipShown = true;

      this.panel.setSkipEnabled(true);
      // Fade in the "tap to continue" hint at the same moment dismiss
      // becomes active. Fire-and-forget so input isn't blocked while
      // the hint is still fading in. Mirrors BigWinView's pattern.
      void this.panel.fadeInContinueText();

      await this.waitForClose();
      if (this.tuning.closeSoundId) this.audioService.presentation.playSoundId?.(this.tuning.closeSoundId);

      onDismissed?.();

      await this.panel.playExitAnimation();

      if (!isRetrigger) {
        // Per-feature free-spins loop when configured (e.g. vs_rush_buy → its own track),
        // else the shared default loop.
        const loopId = this.tuning.music.loopIdByFeatureId?.[featureId] ?? this.tuning.music.loopId;
        this.audioService.control.setMusicTrack(loopId, this.tuning.music.loopFadeMs);
      }

      this.panel.hide();
      this.log(isRetrigger ? "[FeatureTrigger] closed (retrigger)" : "[FeatureTrigger] closed");
    } finally {
      this.unbindInput();
      this.skipShown = false;
      this.panel.setSkipEnabled(false);
      this.interactionBlockStore.remove("feature_trigger_panel");
      releaseOverlay();
    }
  }

  /**
   * Push the per-trigger content to the panel: the raw `featureId` + the raw free-spins `amount`
   * (the count, stringified). The count is the ONLY datum the shell owns; it does NOT localize any
   * surrounding wording — the game renders the count and builds every text line itself (e.g.
   * "You Have Won" / "Free Spins") from `featureId` + its own localization keys.
   */
  private applyContent(featureId: string, featureCount: number | string): void {
    this.panel.setContent({ featureId, amount: String(featureCount) });
  }

  private bindInput(): void {
    this.unbindInput();

    const onPress = (): void => {
      if (!this.skipShown) return;

      this.closeRequested = true;
      this.log("[FeatureTrigger] close requested");
      this.resolveClose?.();
    };

    this.unbindSkipTap = this.panel.bindSkipTap(onPress);
    this.pressBinding.bindKeys(onPress);
  }

  private unbindInput(): void {
    this.unbindSkipTap?.();
    this.unbindSkipTap = null;
    this.pressBinding.unbindAll();
  }

  private waitForClose(): Promise<void> {
    if (this.closeRequested) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.resolveClose = resolve;
    });
  }
}
